import { Commit, Dispatch } from 'vuex'
import { createStripePortalSession, getStripeSubscription, refreshStripeSubscription } from '@/utils/api'
import { isApiError, StripePortalSessionResponse, StripeSubscriptionResponse } from '@/types/api'

export interface SubscriptionState {
  loaded: boolean
  loading: boolean
  availabilityChecked: boolean
  stripeEnabled: boolean
  customerId: string | null
  subscriptionId: string | null
  subscriptionStatus: string | null
  priceId: string | null
  productId: string | null
  currentPeriodEnd: number | null
  cancelAtPeriodEnd: boolean
  hasAccess: boolean
  billingConfigured: boolean
  pricingTableId: string
  publishableKey: string
}

const initialState = (): SubscriptionState => ({
  loaded: false,
  loading: false,
  availabilityChecked: false,
  stripeEnabled: false,
  customerId: null,
  subscriptionId: null,
  subscriptionStatus: null,
  priceId: null,
  productId: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  hasAccess: false,
  billingConfigured: false,
  pricingTableId: process.env.VUE_APP_STRIPE_PRICING_TABLE_ID || '',
  publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || ''
})

export const subscription = () => ({
  namespaced: true,
  state: initialState(),
  mutations: {
    setLoading(state: SubscriptionState, value: boolean) {
      state.loading = value
    },
    setStripeAvailability(state: SubscriptionState, value: boolean) {
      state.availabilityChecked = true
      state.stripeEnabled = value
    },
    setState(state: SubscriptionState, payload: StripeSubscriptionResponse) {
      state.loaded = true
      state.availabilityChecked = true
      state.stripeEnabled = true
      state.customerId = payload.customerId
      state.subscriptionId = payload.subscriptionId
      state.subscriptionStatus = payload.subscriptionStatus
      state.priceId = payload.priceId
      state.productId = payload.productId
      state.currentPeriodEnd = payload.currentPeriodEnd
      state.cancelAtPeriodEnd = payload.cancelAtPeriodEnd
      state.hasAccess = payload.hasAccess
      state.billingConfigured = payload.billingConfigured
      state.pricingTableId = process.env.VUE_APP_STRIPE_PRICING_TABLE_ID || ''
      state.publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || ''
    },
    clear(state: SubscriptionState) {
      Object.assign(state, initialState())
    }
  },
  actions: {
    async ensureAvailabilityChecked({ state, dispatch }: { state: SubscriptionState; dispatch: Dispatch }) {
      if (state.availabilityChecked || state.loading) {
        return
      }
      await dispatch('checkAvailability')
    },
    async checkAvailability({ commit }: { commit: Commit }) {
      const response = await getStripeSubscription()
      if (isApiError(response)) {
        const statusCode = response.statusCode
        commit('setStripeAvailability', typeof statusCode === 'number' ? statusCode < 404 : false)
        return typeof statusCode === 'number' ? statusCode < 404 : false
      }
      commit('setStripeAvailability', true)
      return true
    },
    async ensureLoaded({ state, dispatch }: { state: SubscriptionState; dispatch: Dispatch }) {
      if (state.loaded || state.loading) {
        return
      }
      await dispatch('refresh')
    },
    async refresh({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setLoading', true)
      const response = await refreshStripeSubscription()
      if (isApiError(response)) {
        const fallback = await getStripeSubscription()
        if (isApiError(fallback)) {
          commit('setLoading', false)
          dispatch(
            'addLogEvent',
            { title: 'Stripe Subscription Error', content: fallback },
            { root: true }
          )
          return fallback
        }
        commit('setState', fallback)
        commit('setLoading', false)
        return fallback
      }
      commit('setState', response)
      commit('setLoading', false)
      return response
    },
    clear({ commit }: { commit: Commit }) {
      commit('clear')
    },
    async createPortalSession({ dispatch }: { dispatch: Dispatch }): Promise<StripePortalSessionResponse | null> {
      const response = await createStripePortalSession()
      if (isApiError(response)) {
        dispatch(
          'addLogEvent',
          { title: 'Stripe Portal Session Error', content: response },
          { root: true }
        )
        return null
      }
      return response
    }
  },
  getters: {
    stripeEnabled: (state: SubscriptionState) => state.stripeEnabled,
    hasAccess: (state: SubscriptionState) => state.hasAccess,
    customerId: (state: SubscriptionState) => state.customerId,
    subscriptionId: (state: SubscriptionState) => state.subscriptionId,
    subscriptionStatus: (state: SubscriptionState) => state.subscriptionStatus,
    priceId: (state: SubscriptionState) => state.priceId,
    productId: (state: SubscriptionState) => state.productId,
    currentPeriodEnd: (state: SubscriptionState) => state.currentPeriodEnd,
    cancelAtPeriodEnd: (state: SubscriptionState) => state.cancelAtPeriodEnd,
    billingConfigured: (state: SubscriptionState) => state.billingConfigured,
    pricingTableId: (state: SubscriptionState) => state.pricingTableId,
    publishableKey: (state: SubscriptionState) => state.publishableKey,
    isSubscribed: (state: SubscriptionState) => state.hasAccess
  }
})
